import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../store/store";
import {
  fetchBlockedSites,
  fetchBlockedCategories,
  fetchBlockingStats,
  addBlockedSite,
  removeBlockedSite,
  addBlockedCategory,
  removeBlockedCategory,
} from "../store/slices/blockingSlice";
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusIcon,
  ArrowPathIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

const Blocking: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    blockedSites,
    blockedCategories,
    availableCategories,
    stats,
    isLoading,
  } = useSelector((state: RootState) => state.blocking);

  const [activeTab, setActiveTab] = useState<"sites" | "categories" | "ads">(
    "sites"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState("");
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    dispatch(fetchBlockedSites({}));
    dispatch(fetchBlockedCategories({}));
    dispatch(fetchBlockingStats());
  }, [dispatch]);

  const handleBlockItem = async () => {
    if (!newItem.trim()) {
      alert("Please enter a value");
      return;
    }

    try {
      if (activeTab === "sites") {
        await dispatch(
          addBlockedSite({
            url: newItem,
            reason: reason || "Manual block",
            category: category || undefined,
          })
        ).unwrap();
      } else if (activeTab === "categories") {
        await dispatch(
          addBlockedCategory({
            name: newItem,
            description: reason,
          })
        ).unwrap();
      }

      setNewItem("");
      setReason("");
      setCategory("");
      setShowAddModal(false);
    } catch (error) {
      console.error("Failed to block item:", error);
    }
  };

  const handleUnblockSite = async (id: number) => {
    if (window.confirm("Are you sure you want to unblock this site?")) {
      try {
        await dispatch(removeBlockedSite(id)).unwrap();
      } catch (error) {
        console.error("Failed to unblock site:", error);
      }
    }
  };

  const handleUnblockCategory = async (id: number) => {
    if (window.confirm("Are you sure you want to unblock this category?")) {
      try {
        await dispatch(removeBlockedCategory(id)).unwrap();
      } catch (error) {
        console.error("Failed to unblock category:", error);
      }
    }
  };

  const filteredSites = blockedSites.filter((site) =>
    site.url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCategories = blockedCategories.filter((cat) =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-normal text-gray-900">
            Allow & Block Ads
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Control which ads appear on your site
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => {
              dispatch(fetchBlockedSites({}));
              dispatch(fetchBlockedCategories({}));
              dispatch(fetchBlockingStats());
            }}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
            title="Refresh"
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center space-x-2 bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add to blocklist</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "sites", label: "Sites", icon: ShieldExclamationIcon },
            { id: "categories", label: "Categories", icon: ShieldCheckIcon },
            { id: "ads", label: "Specific Ads", icon: XMarkIcon },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Icon className="h-5 w-5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Search Bar */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search blocked items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Blocked Sites</p>
                <p className="text-2xl font-normal text-gray-900">
                  {stats?.blocked_sites || blockedSites.length}
                </p>
              </div>
              <ShieldExclamationIcon className="h-8 w-8 text-red-500" />
            </div>
            <div className="mt-4 flex items-center text-green-600 text-sm">
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              <span>Protecting your site</span>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Blocked Categories</p>
                <p className="text-2xl font-normal text-gray-900">
                  {stats?.blocked_categories || blockedCategories.length}
                </p>
              </div>
              <ShieldCheckIcon className="h-8 w-8 text-yellow-500" />
            </div>
            <div className="mt-4 flex items-center text-blue-600 text-sm">
              <InformationCircleIcon className="h-4 w-4 mr-1" />
              <span>Filtering {stats?.ads_filtered || 0} ads</span>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Last Updated</p>
                <p className="text-2xl font-normal text-gray-900">
                  {stats?.last_updated
                    ? new Date(stats.last_updated).toLocaleDateString()
                    : "Today"}
                </p>
              </div>
              <ArrowPathIcon className="h-8 w-8 text-green-500" />
            </div>
            <button
              onClick={() => {
                dispatch(fetchBlockingStats());
              }}
              className="mt-4 text-sm text-blue-600 hover:text-blue-700"
            >
              Update now
            </button>
          </div>
        </div>

        {/* Sites Tab */}
        {activeTab === "sites" && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-medium text-gray-900">Blocked Sites</h3>
                <p className="text-sm text-gray-600">
                  These sites won't show ads on your pages
                </p>
              </div>

              {isLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-600">Loading...</p>
                </div>
              ) : filteredSites.length === 0 ? (
                <div className="p-8 text-center">
                  <ShieldCheckIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Sites Blocked
                  </h3>
                  <p className="text-gray-600">
                    You haven't blocked any sites yet
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredSites.map((site) => (
                    <div key={site.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <ShieldExclamationIcon className="h-5 w-5 text-red-500" />
                            <div>
                              <p className="font-medium text-gray-900">
                                {site.url}
                              </p>
                              <div className="flex items-center space-x-4 text-sm text-gray-500">
                                <span>
                                  Blocked on{" "}
                                  {new Date(
                                    site.created_at
                                  ).toLocaleDateString()}
                                </span>
                                <span>•</span>
                                <span>{site.reason}</span>
                                {site.category && (
                                  <>
                                    <span>•</span>
                                    <span>{site.category}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleUnblockSite(site.id)}
                          className="text-sm text-blue-600 hover:text-blue-700"
                        >
                          Unblock
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === "categories" && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-medium text-gray-900">
                  Blocked Categories
                </h3>
                <p className="text-sm text-gray-600">
                  Prevent ads from specific categories
                </p>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Blocked Categories */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">
                      Currently Blocked
                    </h4>
                    <div className="space-y-3">
                      {filteredCategories.map((category) => (
                        <div
                          key={category.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div>
                            <p className="font-medium text-gray-900">
                              {category.name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {category.description}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Added on{" "}
                              {new Date(
                                category.created_at
                              ).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={() => handleUnblockCategory(category.id)}
                            className="text-sm text-blue-600 hover:text-blue-700"
                          >
                            Allow
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Available Categories */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">
                      Available Categories
                    </h4>
                    <div className="space-y-3">
                      {availableCategories.map((cat) => (
                        <div
                          key={cat}
                          className="p-3 border border-gray-200 rounded-lg"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium text-gray-900">{cat}</p>
                            <button
                              onClick={() => {
                                setNewItem(cat);
                                setShowAddModal(true);
                              }}
                              className="text-sm text-blue-600 hover:text-blue-700"
                            >
                              Block
                            </button>
                          </div>
                          <p className="text-sm text-gray-500">
                            Block all ads from this category
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Category Recommendations */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-900">
                    Category Recommendations
                  </h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    Based on your site content, we recommend blocking certain
                    categories to maintain brand safety.
                  </p>
                  <button className="mt-3 text-sm text-yellow-700 hover:text-yellow-800">
                    View recommendations
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Ads Tab */}
        {activeTab === "ads" && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-8 text-center">
              <XMarkIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Block Specific Ads
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                This feature allows you to block specific ads that you've seen
                on your site. Ads will appear here as they are displayed on your
                pages.
              </p>
              <div className="inline-flex items-center space-x-2 text-blue-600">
                <InformationCircleIcon className="h-5 w-5" />
                <span className="text-sm">
                  Ads will appear here once they're displayed on your site
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Add to Blocklist
                </h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewItem("");
                    setReason("");
                    setCategory("");
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {activeTab === "sites" && "Website URL"}
                    {activeTab === "categories" && "Category Name"}
                    {activeTab === "ads" && "Ad URL or ID"}
                  </label>
                  <input
                    type="text"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder={
                      activeTab === "sites"
                        ? "example.com"
                        : activeTab === "categories"
                        ? "Category name"
                        : "Ad identifier"
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason (Optional)
                  </label>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="Why are you blocking this?"
                  />
                </div>

                {activeTab === "sites" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category (Optional)
                    </label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select a category</option>
                      <option value="gambling">Gambling</option>
                      <option value="adult">Adult Content</option>
                      <option value="political">Political</option>
                      <option value="malware">Malware</option>
                      <option value="low-quality">Low Quality</option>
                    </select>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setNewItem("");
                      setReason("");
                      setCategory("");
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBlockItem}
                    disabled={!newItem.trim()}
                    className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${
                      !newItem.trim() ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    Add to Blocklist
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blocking;
